<template>
  <v-card id="acceptance-parties" class="pa-4 mb-4">
    <div class="text-subtitle-1 font-weight-medium mb-3">
      {{ $t("acceptance.parties.title") }}
    </div>

    <div class="d-flex align-center mb-2">
      <span class="text-body-2 font-weight-medium mr-auto">
        {{ $t("acceptance.owner.title") }}
      </span>
      <correction-note
        group="owner"
        part="action"
        :frozen="frozen"
        :corrections="corrections"
        :can-correct="canCorrect"
        :disabled="correctDisabled"
        @correct="$emit('correct', $event)"
        @open-log="$emit('open-log', $event)"
      />
    </div>
    <v-row dense>
      <v-col cols="12" md="6">
        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="$t('acceptance.owner.name')"
          :model-value="values['owner.name']"
          :readonly="readonly || frozen"
          :bg-color="bg('owner.name')"
          maxlength="200"
          @update:model-value="change('owner.name', $event)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="$t('acceptance.owner.tax_code')"
          :model-value="values['owner.taxCode']"
          :readonly="readonly || frozen"
          :bg-color="bg('owner.taxCode')"
          maxlength="32"
          @update:model-value="change('owner.taxCode', $event)"
        />
      </v-col>
    </v-row>
    <correction-note
      group="owner"
      part="history"
      :frozen="frozen"
      :corrections="corrections"
      :can-correct="canCorrect"
      :disabled="correctDisabled"
      @correct="$emit('correct', $event)"
      @open-log="$emit('open-log', $event)"
    />

    <div class="d-flex align-center mb-2 mt-3">
      <span class="text-body-2 font-weight-medium mr-auto">
        {{ $t("acceptance.deliverer.title") }}
      </span>
      <correction-note
        group="deliverer"
        part="action"
        :frozen="frozen"
        :corrections="corrections"
        :can-correct="canCorrect"
        :disabled="correctDisabled"
        @correct="$emit('correct', $event)"
        @open-log="$emit('open-log', $event)"
      />
    </div>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="`${$t('acceptance.deliverer.name')} *`"
          :model-value="values['deliverer.name']"
          :readonly="readonly || frozen"
          :bg-color="bg('deliverer.name')"
          maxlength="200"
          @update:model-value="change('deliverer.name', $event)"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          variant="outlined"
          density="comfortable"
          :clearable="!readonly && !frozen"
          :label="$t('acceptance.deliverer.id_document_type')"
          :items="documentItems"
          :model-value="values['deliverer.idDocument.type']"
          :readonly="readonly || frozen"
          :bg-color="bg('deliverer.idDocument.type')"
          @update:model-value="change('deliverer.idDocument.type', $event)"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="$t('acceptance.deliverer.id_document_number')"
          :model-value="values['deliverer.idDocument.number']"
          :readonly="readonly || frozen"
          :bg-color="bg('deliverer.idDocument.number')"
          maxlength="50"
          @update:model-value="change('deliverer.idDocument.number', $event)"
        />
      </v-col>
    </v-row>
    <correction-note
      group="deliverer"
      part="history"
      :frozen="frozen"
      :corrections="corrections"
      :can-correct="canCorrect"
      :disabled="correctDisabled"
      @correct="$emit('correct', $event)"
      @open-log="$emit('open-log', $event)"
    />

    <div class="d-flex align-center mb-2 mt-3">
      <span class="text-body-2 font-weight-medium mr-auto">
        {{ $t("acceptance.authority.label") }}
      </span>
      <correction-note
        group="authorityToDeliver"
        part="action"
        :frozen="frozen"
        :corrections="corrections"
        :can-correct="canCorrect"
        :disabled="correctDisabled"
        @correct="$emit('correct', $event)"
        @open-log="$emit('open-log', $event)"
      />
    </div>
    <v-row dense>
      <v-col cols="12" md="5">
        <v-select
          variant="outlined"
          density="comfortable"
          :clearable="!readonly && !frozen"
          :label="`${$t('acceptance.authority.label')} *`"
          :items="authorityItems"
          :model-value="values['authorityToDeliver.basis']"
          :readonly="readonly || frozen"
          :bg-color="bg('authorityToDeliver.basis')"
          @update:model-value="change('authorityToDeliver.basis', $event)"
        />
      </v-col>
      <v-col cols="12" md="7">
        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="$t('acceptance.authority.note')"
          :placeholder="$t('acceptance.authority.optional')"
          :model-value="values['authorityToDeliver.note']"
          :readonly="readonly || frozen"
          :bg-color="bg('authorityToDeliver.note')"
          maxlength="500"
          @update:model-value="change('authorityToDeliver.note', $event)"
        />
      </v-col>
    </v-row>
    <correction-note
      group="authorityToDeliver"
      part="history"
      :frozen="frozen"
      :corrections="corrections"
      :can-correct="canCorrect"
      :disabled="correctDisabled"
      @correct="$emit('correct', $event)"
      @open-log="$emit('open-log', $event)"
    />
  </v-card>
</template>

<script>
import CorrectionNote from "@/components/Acceptance/correctionNote.vue";

// PROVISIONAL values, as in the API's api/constants/compliance.js.
const ID_DOCUMENT_TYPES = [
  "identity_card",
  "passport",
  "driving_licence",
  "other",
];
const AUTHORITY_BASES = [
  "is_owner",
  "delegation",
  "legal_representative",
  "other",
];

export default {
  name: "AcceptancePartiesCard",
  components: { "correction-note": CorrectionNote },
  props: {
    values: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    highlight: { type: Array, default: () => [] },
    // Confirmed: some fields are frozen and change through a correction (corrections.mjs).
    frozen: { type: Boolean, default: false },
    corrections: { type: Object, default: () => ({}) },
    canCorrect: { type: Boolean, default: false },
    correctDisabled: { type: Boolean, default: false },
  },
  emits: ["change", "correct", "open-log"],
  computed: {
    documentItems() {
      return ID_DOCUMENT_TYPES.map((value) => ({
        value,
        title: this.$t(`acceptance.id_document_types.${value}`),
      }));
    },
    authorityItems() {
      return AUTHORITY_BASES.map((value) => ({
        value,
        title: this.$t(`acceptance.authority_bases.${value}`),
      }));
    },
  },
  methods: {
    bg(path) {
      return this.highlight.includes(path) ? "amber-lighten-5" : undefined;
    },
    change(path, value) {
      this.$emit("change", path, value);
    },
  },
};
</script>
